import { useMemo, useState } from 'react';
import { ArrowLeft, Heart, Clock, MapPin, Phone, Users, Swords } from 'lucide-react';
import {
  BOOKING_DEFAULT_CONFIG,
  courtDisplayName,
  getEloLevel,
  resolveEffectiveCourts,
} from '../../../engine';
import type { Center } from '../memberData';
import {
  localMs,
  relativeDayLabel,
  splitUpcomingPast,
  planOpenSlots,
  planJoin,
  planLeave,
  isMemberInMatch,
} from '../memberData';
import { Card, EmptyState, SectionTitle, PlayerName } from '../mobileUi';
import { useLocale, type MessageKey } from '../../../i18n';
import { MobileBook } from './MobileBook';
import { MobileActivities } from './MobileActivities';

export interface MobileCenterDetailProps {
  center: Center;
  isFavorite: boolean;
  onFavorite: (id: string | null) => Promise<void> | void;
  onBack: () => void;
  member: any;
  effectivePlayerDB: any[];
  bookings: any[];
  plannedMatches: any[];
  activities: any[];
  bookingConfig: any;
  /** False until the club has saved its opening hours (owner decision 2026-10-31). */
  bookingOpen?: boolean;
  bookingZones: any[];
  clubInfo: any;
  addBooking: (data: any, opts?: any) => Promise<any>;
  /** The venue's equipment and its reservations (#911 slice 2). */
  assets?: any[];
  assetBookings?: any[];
  /** The member equipment command — a machine booked on its own (§ 6b). */
  addAssetBooking?: (intent: any) => Promise<any>;
  persistActivities: (next: any[]) => any;
  /** Adopt an activity the server changed (#245). */
  applyServerActivity: (updated: any) => void;
  /** Planned single matches: register on a booking, join/leave an open slot. */
  addPlannedMatch?: (input: any) => Promise<any>;
  updatePlannedMatchPlayers?: (id: any, teams: { team1: any; team2: any }) => Promise<any>;
  deletePlannedMatch?: (id: any) => Promise<any>;
  initialTab?: CenterTab;
  /** Rendered as the «Spill» tab: no back arrow, a «bytt senter» button instead. */
  asTab?: boolean;
  onSwitchCenter?: () => void;
}

export type CenterTab = 'about' | 'play' | 'activities' | 'matches';

// Module-level data, so it carries KEYS and the label is resolved where it is
// rendered — the same shape as MOBILE_TABS (#310).
const TABS: { id: CenterTab; labelKey: MessageKey }[] = [
  { id: 'about', labelKey: 'mobile.centre.tabAbout' },
  { id: 'play', labelKey: 'mobile.centre.tabBook' },
  { id: 'activities', labelKey: 'mobile.centre.tabActivities' },
  { id: 'matches', labelKey: 'mobile.centre.tabMatches' },
];

// One padel centre: Om (hours, contact, courts) · Spill (book a court) ·
// Aktiviteter (open sign-ups here, level-aware) · Matcher (single matches
// members have put on the calendar here). Booking and activities are the
// same screens as the tabs, embedded and scoped to this centre.
export function MobileCenterDetail({
  center,
  isFavorite,
  onFavorite,
  onBack,
  member,
  effectivePlayerDB,
  bookings,
  plannedMatches,
  activities,
  bookingConfig,
  bookingOpen = true,
  bookingZones,
  clubInfo,
  addBooking,
  assets = [],
  assetBookings = [],
  addAssetBooking,
  persistActivities,
  applyServerActivity,
  addPlannedMatch,
  updatePlannedMatchPlayers,
  initialTab = 'about',
  asTab = false,
  onSwitchCenter,
}: MobileCenterDetailProps) {
  // `tr`, not `t`: the tab table below already reads better with `tab`/`tabDef`,
  // and this file sits beside three others that bind `t` to a tournament (#310).
  const { t: tr, formatDate } = useLocale();
  const [tab, setTab] = useState<CenterTab>(initialTab);
  const [busyPlan, setBusyPlan] = useState<any>(null);
  const cfg = { ...BOOKING_DEFAULT_CONFIG, ...(bookingConfig || {}) };
  const courtMeta = cfg.courtMeta || {};
  // THE «OM» TAB'S COURTS, through the engine's rule (#873), with two inputs
  // withheld ON PURPOSE. `enabledCourts` is not passed, so a court closed for
  // maintenance is still listed among the centre's courts, and the physical
  // count has no default of four, so a centre that never stored one lists
  // none. Both are what this tab has always shown, and whether either should
  // change is a product question rather than a refactor.
  const courts = resolveEffectiveCourts({ courtCount: cfg.courtCount }, clubInfo?.courts);
  const db = useMemo(() => effectivePlayerDB || [], [effectivePlayerDB]);

  // «Matcher» — planned single matches on this centre's courts, soonest first.
  const matches = useMemo(() => {
    if (!center.isHome) return [];
    const items = (plannedMatches || [])
      .filter((p: any) => p.status !== 'cancelled' && p.status !== 'completed')
      .map((p: any) => {
        const b = (bookings || []).find((x: any) => x.id === p.bookingId);
        if (!b) return null;
        return {
          plan: p,
          booking: b,
          startMs: localMs(b.date, b.startTime),
          endMs: localMs(b.date, b.endTime || b.startTime),
        };
      })
      .filter(Boolean) as any[];
    return splitUpcomingPast(items).upcoming;
  }, [plannedMatches, bookings, center.isHome]);

  const today = new Date();
  const todayLabel = formatDate(today, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-2">
        {!asTab && (
          <button
            onClick={onBack}
            aria-label={tr('mobile.common.back')}
            className="p-1 -ml-1 text-ink-3"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <h1
          className="font-display font-semibold text-ink leading-tight truncate flex-1"
          style={{ fontSize: asTab ? 24 : 20 }}
        >
          {center.name}
        </h1>
        {asTab && onSwitchCenter && (
          <button
            onClick={onSwitchCenter}
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold flex-shrink-0"
            style={{ background: '#fff', border: '1px solid var(--line-strong)', color: '#0f1611' }}
          >
            {tr('mobile.centre.switch')}
          </button>
        )}
        <button
          onClick={() => onFavorite(isFavorite ? null : center.id)}
          aria-label={
            isFavorite ? tr('mobile.centres.unfavourite') : tr('mobile.centres.favourite')
          }
          aria-pressed={isFavorite}
          className="p-1.5"
          style={{ color: isFavorite ? '#65a30d' : 'var(--ink-3)' }}
        >
          <Heart size={20} fill={isFavorite ? '#c2f02f' : 'none'} />
        </button>
      </div>

      {/* Tabs */}
      <div
        // The strip keeps the phone's proportions from the rail width rather
        // than spreading four labels across the page (desktop step 3).
        className="flex mt-2 mb-3 mx-4 @rail:max-w-[520px]"
        role="tablist"
        style={{ borderBottom: '1px solid #e7eae3' }}
      >
        {TABS.map((tabDef) => {
          const active = tabDef.id === tab;
          return (
            <button
              key={tabDef.id}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(tabDef.id)}
              className="flex-1 py-2 text-[13px] font-semibold"
              style={{
                color: active ? '#0f1611' : '#6b746d',
                borderBottom: active ? '2px solid #c2f02f' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {tr(tabDef.labelKey)}
            </button>
          );
        })}
      </div>

      {tab === 'about' && (
        // Two columns of the same sections on a wide screen, in reading order.
        <div className="px-4 space-y-4 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-x-8 @wide:gap-y-4 @wide:items-start">
          <div>
            <SectionTitle>{tr('mobile.centre.openingHours')}</SectionTitle>
            <Card>
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ width: 34, height: 34, background: '#0f1611', color: '#fff' }}
                >
                  <Clock size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-ink">
                    {tr('mobile.centre.today')}
                  </div>
                  <div className="text-[11px] text-ink-3 capitalize">{todayLabel}</div>
                </div>
                <div className="font-mono font-bold text-[14px] text-lime-deep tabular-nums">
                  {cfg.openTime}–{cfg.closeTime}
                </div>
              </div>
            </Card>
          </div>

          {center.isHome && (clubInfo?.address || clubInfo?.phone || clubInfo?.venue) && (
            <div>
              <SectionTitle>{tr('mobile.centre.contact')}</SectionTitle>
              <Card>
                <div className="space-y-1.5 text-[13px] text-ink-2">
                  {clubInfo?.venue && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-ink-3" /> {clubInfo.venue}
                    </div>
                  )}
                  {clubInfo?.address && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-ink-3" /> {clubInfo.address}
                    </div>
                  )}
                  {clubInfo?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-ink-3" /> {clubInfo.phone}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          <div>
            <SectionTitle>{tr('mobile.centre.courts')}</SectionTitle>
            {courts.length === 0 ? (
              <EmptyState title={tr('mobile.centre.noCourts')} />
            ) : (
              <div className="space-y-2">
                {courts.map((c) => (
                  <Card key={c}>
                    <div className="flex items-center gap-3">
                      <div
                        className="flex items-center justify-center rounded-xl flex-shrink-0 font-display font-semibold"
                        style={{ width: 40, height: 40, background: '#eef0ea', fontSize: 15 }}
                      >
                        {c}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-ink">
                          {courtDisplayName(c, courtMeta)}
                        </div>
                        <div className="text-[11px] text-ink-3">
                          {tr('mobile.centre.courtKind')}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div className="text-[11px] text-ink-3 px-1 flex items-center gap-1">
            <Users size={12} />{' '}
            {center.openCount === 1
              ? tr('mobile.centre.openActivityHere', { count: center.openCount })
              : tr('mobile.centre.openActivitiesHere', { count: center.openCount })}
          </div>
        </div>
      )}

      {tab === 'play' &&
        (center.isHome ? (
          <MobileBook
            member={member}
            bookings={bookings}
            bookingConfig={bookingConfig}
            bookingOpen={bookingOpen}
            bookingZones={bookingZones}
            clubInfo={clubInfo}
            addBooking={addBooking}
            // Only the HOME venue's machines: this screen books the home
            // centre's courts, and the equipment is that venue's.
            assets={assets}
            assetBookings={assetBookings}
            addAssetBooking={addAssetBooking}
            onRegisterMatch={
              addPlannedMatch && member
                ? (booking: any, opts: any) =>
                    addPlannedMatch({
                      bookingId: booking.id,
                      team1: { p1Id: member.id, p2Id: null },
                      team2: { p1Id: null, p2Id: null },
                      eloWeightPct: 100,
                      matchFormat: 'bo3_super',
                      deuceMode: 'advantage',
                      levelMin: opts?.levelMin ?? null,
                      levelMax: opts?.levelMax ?? null,
                    })
                : undefined
            }
            embedded
          />
        ) : (
          <div className="px-4">
            <EmptyState
              title={tr('mobile.centre.bookingComingTitle')}
              hint={tr('mobile.centre.bookingComingHint')}
            />
          </div>
        ))}

      {tab === 'activities' && (
        <MobileActivities
          member={member}
          activities={activities}
          bookings={bookings}
          clubInfo={clubInfo}
          centerFilter={center.id}
          centerName={center.name}
          persistActivities={persistActivities}
          applyServerActivity={applyServerActivity}
          embedded
        />
      )}

      {tab === 'matches' && (
        // Match cards two-up on a wide screen; the lead and an empty state span both.
        <div className="px-4 space-y-3 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-3 @wide:items-start @wide:[&>p]:col-span-2 @wide:[&>p+div:last-child]:col-span-2">
          <p className="text-[12px] text-ink-3 px-1">{tr('mobile.centre.matchesLead')}</p>
          {matches.length === 0 ? (
            <EmptyState
              icon={<Swords size={22} />}
              title={tr('mobile.centre.noMatchesTitle')}
              hint={tr('mobile.centre.noMatchesHint')}
            />
          ) : (
            matches.map((m: any) => {
              const p = m.plan;
              const open = planOpenSlots(p);
              const inIt = isMemberInMatch(p, member?.id);
              const busy = busyPlan === p.id;
              const name = (id: any) => <PlayerName db={db} id={id} meId={member?.id} />;
              // Level gate set by the booker (absent = anyone).
              const memberLevel = getEloLevel(Math.round(member?.elo || 0)).level;
              const hasGate = p.levelMin != null || p.levelMax != null;
              const levelOk =
                (p.levelMin == null || memberLevel >= Number(p.levelMin)) &&
                (p.levelMax == null || memberLevel <= Number(p.levelMax));
              const canJoin =
                !!updatePlannedMatchPlayers && !!member && !inIt && open > 0 && levelOk;
              const canLeave = !!updatePlannedMatchPlayers && !!member && inIt;
              const mutate = async (teams: { team1: any; team2: any } | null) => {
                if (!teams || !updatePlannedMatchPlayers) return;
                setBusyPlan(p.id);
                try {
                  await updatePlannedMatchPlayers(p.id, teams);
                } finally {
                  setBusyPlan(null);
                }
              };
              return (
                <Card key={p.id} accent={inIt ? '#c2f02f' : open > 0 ? '#0ea5e9' : '#e7eae3'}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
                      {relativeDayLabel(m.booking.date)} · {m.booking.startTime}–{m.booking.endTime}{' '}
                      · {courtDisplayName(m.booking.court, courtMeta)}
                    </span>
                    <span
                      className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{
                        background: inIt ? '#f7fdde' : open > 0 ? '#fef9c3' : '#eef0ea',
                        color: inIt ? '#3f6212' : open > 0 ? '#854d0e' : '#4a544c',
                      }}
                    >
                      {inIt
                        ? 'DU ER MED'
                        : open > 0
                          ? `${open} LEDIG${open === 1 ? '' : 'E'}`
                          : 'FULL'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="text-[13px] font-semibold text-ink truncate">
                      {name(p.team1?.p1Id)}/{name(p.team1?.p2Id)}
                    </div>
                    <span className="font-mono text-[11px] text-ink-3">vs</span>
                    <div className="text-[13px] font-semibold text-ink truncate text-right">
                      {name(p.team2?.p1Id)}/{name(p.team2?.p2Id)}
                    </div>
                  </div>
                  <div className="text-[11px] text-ink-3 mt-1">
                    {hasGate && (
                      <span
                        className={`font-mono font-bold mr-2 ${levelOk || inIt ? '' : 'text-rose'}`}
                      >
                        {tr('mobile.centre.levelGate', {
                          min: String(p.levelMin ?? 1),
                          max: String(p.levelMax ?? 9),
                        })}
                      </span>
                    )}
                    {p.eloWeightPct === 0
                      ? tr('mobile.centre.friendlyMatch')
                      : p.eloWeightPct != null && p.eloWeightPct !== 100
                        ? tr('mobile.centre.countsTowardsEloPct', {
                            pct: String(p.eloWeightPct),
                          })
                        : tr('mobile.centre.countsTowardsElo')}
                  </div>
                  {!inIt && open > 0 && !levelOk && (
                    <div
                      className="mt-2.5 rounded-xl px-3 py-2 text-[12px]"
                      style={{ background: '#ffe4e6', color: '#be123c' }}
                    >
                      {tr('mobile.centre.outsideLevel', { level: String(memberLevel) })}
                    </div>
                  )}
                  {(canJoin || canLeave) && (
                    <button
                      onClick={() =>
                        mutate(canLeave ? planLeave(p, member.id) : planJoin(p, member.id))
                      }
                      disabled={busy}
                      className="mt-2.5 w-full rounded-xl py-2.5 text-[13px] font-semibold disabled:opacity-40 active:scale-[0.98] transition-transform"
                      style={
                        canLeave
                          ? { background: '#fff', color: '#be123c', border: '1px solid #fecdd3' }
                          : { background: '#0f1611', color: '#fff' }
                      }
                    >
                      {busy
                        ? tr('mobile.common.saving')
                        : canLeave
                          ? tr('mobile.centre.leaveMatch')
                          : tr('mobile.centre.joinMatch')}
                    </button>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
